// client/src/lib/api/users-api.ts
// ===========================================
// MÓDULO DE GESTIÓN DE USUARIOS DE TIENDA
// Maneja la obtención, creación, actualización y eliminación de usuarios.
// Usa apiRequest para inyectar headers y manejar errores de forma tipada.
// ===========================================

import { apiRequest } from "./api-client";

// -------------------------------------------
// Obtener usuarios de una tienda
// -------------------------------------------
// COMENTARIO: apiRequest inyecta automáticamente x-user-id y x-shop-id.
// Extraemos response.users para mantener compatibilidad con componentes existentes.
export async function getShopUsers(shopId: string): Promise<any[]> {
  const response = await apiRequest<{ users: any[] }>(
    `/api/shops/${shopId}/users`,
  );
  return response.users;
}

// -------------------------------------------
// Crear un nuevo usuario
// -------------------------------------------
// COMENTARIO: Se elimina el bloque manual que hacía (error as any).status = res.status.
// Ahora, si el backend falla, apiRequest lanza ApiError automáticamente, exponiendo
// error.status y error.data para que el frontend los consuma si es necesario.
export async function createUser(userData: {
  shopId: string;
  username: string;
  password: string;
  fullName: string;
  email?: string;
  phone?: string;
  role: "store_manager" | "cashier";
}): Promise<any> {
  const response = await apiRequest<{ user: any }>(
    `/api/shops/${userData.shopId}/users`,
    {
      method: "POST",
      body: JSON.stringify(userData),
    },
  );
  return response.user;
}

// -------------------------------------------
// Actualizar un usuario existente
// -------------------------------------------
export async function updateUser(
  shopId: string,
  userId: string,
  updates: {
    username?: string;
    fullName?: string;
    email?: string;
    phone?: string;
    role?: "store_manager" | "cashier";
    isActive?: boolean;
    password?: string;
  },
): Promise<any> {
  const response = await apiRequest<{ user: any }>(
    `/api/shops/${shopId}/users/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
  return response.user;
}

// -------------------------------------------
// Eliminar un usuario
// -------------------------------------------
// -------------------------------------------
// Obtener supervisores de un owner
// -------------------------------------------
export async function getOwnerSupervisors(ownerId: string): Promise<any[]> {
  const response = await apiRequest<{ supervisors: any[] }>(
    `/api/owners/${ownerId}/supervisors`,
  );
  return response.supervisors;
}

// -------------------------------------------
// Crear supervisor (solo desde Owner Dashboard)
// -------------------------------------------
export async function createOwnerSupervisor(
  ownerId: string,
  data: {
    shopId: string;
    username: string;
    password: string;
    fullName: string;
    email?: string;
    phone?: string;
  },
): Promise<any> {
  const response = await apiRequest<{ user: any }>(
    `/api/owners/${ownerId}/supervisors`,
    {
      method: "POST",
      body: JSON.stringify(data),
    },
  );
  return response.user;
}

// -------------------------------------------
// Actualizar supervisor (solo desde Owner Dashboard)
// -------------------------------------------
export async function updateOwnerSupervisor(
  ownerId: string,
  userId: string,
  updates: {
    fullName?: string;
    username?: string;
    email?: string;
    phone?: string;
    password?: string;
  },
): Promise<any> {
  const response = await apiRequest<{ user: any }>(
    `/api/owners/${ownerId}/supervisors/${userId}`,
    {
      method: "PATCH",
      body: JSON.stringify(updates),
    },
  );
  return response.user;
}

// -------------------------------------------
// Eliminar supervisor (solo desde Owner Dashboard)
// -------------------------------------------
export async function deleteOwnerSupervisor(
  ownerId: string,
  userId: string,
): Promise<void> {
  return apiRequest<void>(`/api/owners/${ownerId}/supervisors/${userId}`, {
    method: "DELETE",
  });
}

export async function deleteUser(
  shopId: string,
  userId: string,
): Promise<void> {
  return apiRequest<void>(`/api/shops/${shopId}/users/${userId}`, {
    method: "DELETE",
  });
}
